import { Link } from '@/components/LocalizedLink';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import DirectAnswerBlock from '@/components/knowledge/DirectAnswerBlock';
import { useLanguage } from '@/contexts/LanguageContext';
import { FIGTURES_LINKEDIN_COMPANY_URL, FIGTURES_INSTAGRAM_URL } from '@/lib/entityProfiles';
import { getAiAnswerEntryById } from '@/lib/aiAnswersCatalog';
import { SITE_URL, buildAbsoluteUrl, truncateMeta } from '@/lib/seo';
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  buildSpeakableSchema,
} from '@/lib/schema';

const PAGE_LAST_UPDATED = '2026-07-31';

const LinkedInCompanyPage = () => {
  const { lang } = useLanguage();
  const canonicalPath = '/linkedin';
  const aiEntry = getAiAnswerEntryById('figtures-linkedin', lang);

  const copy =
    lang === 'tr'
      ? {
          seoTitle: 'Figtures LinkedIn — Resmi Şirket Sayfası',
          seoDescription:
            'Figtures’ın resmi LinkedIn şirket sayfası, product studio tanımı ve figtures.com ile bağlantısı. Ücretsiz LinkedIn metin aracından farklıdır.',
          tag: 'MARKA · LINKEDIN',
          h1: 'Figtures on LinkedIn',
          lead: 'Figtures’ın resmi LinkedIn şirket profili — İstanbul merkezli product studio tanımı, hizmetler ve figtures.com bağlantısı burada.',
          directAnswer:
            aiEntry?.directAnswerText ??
            'Figtures’ın resmi LinkedIn şirket sayfası linkedin.com/company/figtures adresindedir. Bu sayfa Figtures’ın LinkedIn varlığını figtures.com üzerinde açıklar; /tools/linkedin-about-generator ücretsiz metin aracıdır.',
          onLinkedInTitle: 'LinkedIn’de bizi takip edin',
          onLinkedInBody:
            'Şirket sayfamızda product studio tanımı, hizmet kapsamı, teknoloji stack’i ve iletişim bilgileri (contact@figtures.com · figtures.com) yer alır.',
          cta: 'LinkedIn şirket sayfasını aç',
          notToolTitle: 'LinkedIn aracı değil',
          notToolBody:
            'Ücretsiz LinkedIn “Hakkında” ve niyet mektubu üreticimiz ayrı bir araçtır — şirket profili değildir:',
          toolLink: '/tools/linkedin-about-generator',
          toolLabel: 'LinkedIn About aracı →',
          moreTitle: 'Daha fazla',
          about: 'Figtures hakkında',
          contact: 'İletişim',
          aiAnswer: 'AI cevapları hub’ı',
          home: 'Ana Sayfa',
          breadcrumb: 'LinkedIn',
        }
      : {
          seoTitle: 'Figtures on LinkedIn — Official Company Page',
          seoDescription:
            'Figtures’ official LinkedIn company profile, product studio positioning, and link to figtures.com — distinct from the free LinkedIn About generator tool.',
          tag: 'BRAND · LINKEDIN',
          h1: 'Figtures on LinkedIn',
          lead: 'Figtures’ official LinkedIn company profile — Istanbul-based product studio, services, and figtures.com link.',
          directAnswer:
            aiEntry?.directAnswerText ??
            'Figtures’ official LinkedIn company page is at linkedin.com/company/figtures. This figtures.com page describes that presence; /tools/linkedin-about-generator is a separate free writing tool.',
          onLinkedInTitle: 'Follow us on LinkedIn',
          onLinkedInBody:
            'Our company page covers product studio positioning, services, technology stack, and contact details (contact@figtures.com · figtures.com).',
          cta: 'Open LinkedIn company page',
          notToolTitle: 'Not the LinkedIn writing tool',
          notToolBody:
            'Our free LinkedIn About & cover letter generator is a separate product — not the company profile:',
          toolLink: '/tools/linkedin-about-generator',
          toolLabel: 'LinkedIn About tool →',
          moreTitle: 'More',
          about: 'About Figtures',
          contact: 'Contact',
          aiAnswer: 'AI answers hub',
          home: 'Home',
          breadcrumb: 'LinkedIn',
        };

  const faqSchema = buildFaqSchema([
    {
      question: lang === 'tr' ? 'Figtures LinkedIn şirket sayfası nerede?' : 'What is Figtures’ official LinkedIn company page?',
      answer: copy.directAnswer,
    },
    {
      question:
        lang === 'tr'
          ? 'Figtures LinkedIn About aracı ile şirket sayfası aynı mı?'
          : 'Is the Figtures LinkedIn About tool the same as the company page?',
      answer:
        lang === 'tr'
          ? 'Hayır. linkedin.com/company/figtures resmi şirket profilidir; /tools/linkedin-about-generator ücretsiz bir metin aracıdır.'
          : 'No. linkedin.com/company/figtures is the official company profile; /tools/linkedin-about-generator is a free writing tool.',
    },
  ]);

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: copy.home, item: SITE_URL },
    { name: copy.breadcrumb, item: buildAbsoluteUrl(SITE_URL, canonicalPath) },
  ]);

  const orgSchema = buildOrganizationSchema(copy.seoDescription);

  return (
    <main className="pt-32 pb-20">
      <SEOHead
        title={copy.seoTitle}
        description={truncateMeta(copy.seoDescription)}
        canonical={canonicalPath}
        type="article"
        jsonLd={[orgSchema, breadcrumbSchema, faqSchema, buildSpeakableSchema({ canonicalPath, locale: lang })]}
      />

      <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mb-10 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {copy.home}
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <p className="font-body text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">{copy.tag}</p>
          <h1 className="font-display text-3xl lg:text-5xl font-bold text-foreground leading-tight mb-4">{copy.h1}</h1>
          <p className="font-body text-base text-muted-foreground mb-8">{copy.lead}</p>
          <DirectAnswerBlock answer={copy.directAnswer} />
        </motion.header>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mb-10 rounded-2xl border border-border/70 bg-card/30 p-8"
        >
          <h2 className="font-display text-xl text-foreground mb-3">{copy.onLinkedInTitle}</h2>
          <p className="font-body text-base leading-relaxed text-foreground/85 mb-6">{copy.onLinkedInBody}</p>
          <a
            href={FIGTURES_LINKEDIN_COMPANY_URL}
            target="_blank"
            rel="noopener noreferrer me"
            className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 font-body text-sm font-medium text-background hover:opacity-90 transition-opacity"
          >
            {copy.cta}
            <ExternalLink size={16} />
          </a>
          <p className="font-body text-xs text-muted-foreground mt-4 break-all">{FIGTURES_LINKEDIN_COMPANY_URL}</p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="mb-10 rounded-2xl border border-dashed border-border/60 p-6"
        >
          <h2 className="font-display text-lg text-foreground mb-2">{copy.notToolTitle}</h2>
          <p className="font-body text-sm text-muted-foreground mb-4">{copy.notToolBody}</p>
          <Link to={copy.toolLink} className="font-body text-sm text-foreground underline underline-offset-4">
            {copy.toolLabel}
          </Link>
        </motion.section>

        <section className="border-t border-border/70 pt-10">
          <h2 className="font-display text-xl text-foreground mb-4">{copy.moreTitle}</h2>
          <ul className="space-y-2 font-body text-sm">
            <li>
              <Link to="/about" className="text-muted-foreground hover:text-foreground underline underline-offset-4">
                {copy.about}
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-muted-foreground hover:text-foreground underline underline-offset-4">
                {copy.contact}
              </Link>
            </li>
            <li>
              <Link
                to="/ai-answers/figtures-linkedin"
                className="text-muted-foreground hover:text-foreground underline underline-offset-4"
              >
                {copy.aiAnswer}
              </Link>
            </li>
            <li>
              <a
                href={FIGTURES_INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer me"
                className="text-muted-foreground hover:text-foreground underline underline-offset-4"
              >
                Instagram
              </a>
            </li>
          </ul>
          <p className="font-body text-xs text-muted-foreground mt-8">
            {lang === 'tr' ? 'Son güncelleme:' : 'Last updated:'}{' '}
            {new Date(PAGE_LAST_UPDATED).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </section>
      </div>
    </main>
  );
};

export default LinkedInCompanyPage;
